import { memo, useCallback, useMemo, type ReactNode } from 'react';
import { Text, Tooltip, mergeClasses } from '@fluentui/react-components';
import { useMonitoringListItemStyles } from './MonitoringListItem.styles';

/**
 * Status values supported by the shared monitoring list row. Superset of the
 * run statuses (adds `skipped` for trigger evaluations).
 */
export type MonitoringListItemStatus =
  | 'succeeded'
  | 'failed'
  | 'running'
  | 'cancelled'
  | 'skipped';

type MonitoringListItemProps = {
  /** Row id — passed back to onSelect / onRef so a single parent ref-callback can address many rows. */
  id: string;
  /** Drives the colored status dot on the left. */
  status: MonitoringListItemStatus;
  /** Bold primary text (e.g. relative time, identifier). */
  primaryText: ReactNode;
  /** Optional badge or chip rendered to the right of primaryText (e.g. RunStatusBadge, "Fired"/"Skipped"). */
  badge?: ReactNode;
  /** Optional secondary line under the primary row (e.g. exact datetime · draft · turns). */
  metaText?: ReactNode;
  /** Optional trailing right-aligned text (typically a duration). */
  trailingText?: ReactNode;
  isSelected: boolean;
  isFocused?: boolean;
  onSelect: (id: string) => void;
  onRef?: ((id: string, el: HTMLDivElement | null) => void) | undefined;
  /** Wrap the row in a tooltip — useful for surfacing the full timestamp on hover. */
  tooltipContent?: string;
  /** Accessible label for the row when primaryText alone isn't sufficient. */
  ariaLabel?: string;
};

const MonitoringListItemComponent = ({
  id,
  status,
  primaryText,
  badge,
  metaText,
  trailingText,
  isSelected,
  isFocused,
  onSelect,
  onRef,
  tooltipContent,
  ariaLabel,
}: MonitoringListItemProps) => {
  const styles = useMonitoringListItemStyles();

  // O(1) status→class lookup, memoed on the (stable) styles reference.
  const statusClass = useMemo<Record<MonitoringListItemStatus, string>>(
    () => ({
      succeeded: styles.statusSucceeded,
      failed: styles.statusFailed,
      running: styles.statusRunning,
      cancelled: styles.statusCancelled,
      skipped: styles.statusSkipped,
    }),
    [styles]
  )[status];

  const handleRef = useCallback(
    (el: HTMLDivElement | null) => onRef?.(id, el),
    [id, onRef]
  );

  const handleClick = useCallback(() => onSelect(id), [id, onSelect]);

  const row = (
    <div
      ref={handleRef}
      role="option"
      aria-selected={isSelected}
      aria-label={ariaLabel}
      tabIndex={isFocused === false ? -1 : 0}
      className={mergeClasses(
        styles.row,
        status === 'failed' && styles.rowFailed,
        isSelected && styles.rowSelected,
        isFocused && styles.rowFocused
      )}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          handleClick();
        }
      }}
    >
      <div className={mergeClasses(styles.statusIndicator, statusClass)} />
      <div className={styles.info}>
        <div className={styles.primaryRow}>
          <Text className={styles.primaryText}>{primaryText}</Text>
          {badge}
        </div>
        {metaText !== undefined && metaText !== null && (
          <Text className={styles.metaText}>{metaText}</Text>
        )}
      </div>
      {trailingText !== undefined && trailingText !== null && (
        <Text className={styles.trailingText}>{trailingText}</Text>
      )}
    </div>
  );

  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent} relationship="description" positioning="before">
        {row}
      </Tooltip>
    );
  }

  return row;
};

export const MonitoringListItem = memo(MonitoringListItemComponent);
